import { z } from 'zod'

const permissionKeySchema = z.string().min(1)
const eventTimestampSchema = z.string().min(1)
const notificationSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1),
    message: z.string().min(1),
    type: z.string().min(1),
    referenceId: z.string().uuid().nullable(),
    isRead: z.boolean(),
    readAt: eventTimestampSchema.nullable(),
    createdAt: eventTimestampSchema,
})

const systemSettingsUpdatedPayloadSchema = z.object({
    companyName: z.string().min(1),
    logoSrc: z.string().optional(),
    logoSrc64: z.string().optional(),
    isMaintenanceModeEnabled: z.boolean().optional(),
    maintenanceStartsAt: z.string().optional(),
    updatedAt: eventTimestampSchema,
})

const maintenanceModeChangedPayloadSchema = z.object({
    isMaintenanceModeEnabled: z.boolean(),
    isMaintenanceModeActive: z.boolean(),
    isMaintenanceModeScheduled: z.boolean(),
    maintenanceStartsAt: z.string().nullable(),
    updatedAt: eventTimestampSchema,
})

const maintenanceExtensionResolvedPayloadSchema = z.object({
    requestId: z.string().uuid(),
    userId: z.string().uuid(),
    status: z.enum(['approved', 'rejected']),
    maintenanceStartsAt: z.string().nullable(),
    resolvedAt: eventTimestampSchema,
})

const maintenanceExtensionRequestedPayloadSchema = z.object({
    request: z.object({
        id: z.string().uuid(),
        reason: z.string().min(10).max(500),
        requestedAt: eventTimestampSchema,
        user: z.object({
            id: z.string().uuid(),
            username: z.string().min(1),
        }),
    }),
})

const userUpdatedPayloadSchema = z.object({
    userId: z.string().uuid(),
    username: z.string().min(1),
    previousUsername: z.string().min(1),
    permissionKeys: z.array(permissionKeySchema),
    previousPermissionKeys: z.array(permissionKeySchema),
    addedPermissionKeys: z.array(permissionKeySchema),
    removedPermissionKeys: z.array(permissionKeySchema),
    updatedAt: eventTimestampSchema,
})

const notificationCreatedPayloadSchema = z.object({
    userId: z.string().uuid(),
    notification: notificationSchema,
})

const userAccessRevokedPayloadSchema = z.object({
    userId: z.string().uuid(),
    reason: z.enum(['deleted', 'deactivated']),
    updatedAt: eventTimestampSchema,
})

const auditLogCreatedPayloadSchema = z.object({
    auditLogId: z.string().uuid(),
    createdAt: eventTimestampSchema,
})

const apiRequestLogCreatedPayloadSchema = z.object({
    apiRequestLogId: z.string().uuid(),
    websiteId: z.string().uuid().nullable(),
    extensionCount: z.number().int().nonnegative(),
    createdAt: eventTimestampSchema,
})

const updaterUpdatesChangedPayloadSchema = z.object({
    websiteId: z.string().uuid().nullable(),
    status: z.enum(['pending', 'completed', 'failed', 'all']),
    changedAt: eventTimestampSchema,
})

const updaterTransferChangedPayloadSchema = z.object({
    transferId: z.string().uuid(),
    websiteId: z.string().uuid(),
    extensionId: z.string().uuid(),
    websiteName: z.string().min(1),
    extensionName: z.string().min(1),
    stage: z.enum(['queued', 'backup', 'update', 'completed', 'failed']),
    step: z.number().int().min(0).max(2),
    totalSteps: z.number().int().min(1).max(2),
    progress: z.number().min(0).max(100).optional(),
    message: z.string().min(1),
    error: z.string().nullable(),
    changedAt: eventTimestampSchema,
})

export const realtimeEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('system-settings.updated'),
        payload: systemSettingsUpdatedPayloadSchema,
    }),
    z.object({
        type: z.literal('maintenance-mode.changed'),
        payload: maintenanceModeChangedPayloadSchema,
    }),
    z.object({
        type: z.literal('maintenance-extension.resolved'),
        payload: maintenanceExtensionResolvedPayloadSchema,
    }),
    z.object({
        type: z.literal('maintenance-extension.requested'),
        payload: maintenanceExtensionRequestedPayloadSchema,
    }),
    z.object({
        type: z.literal('user.updated'),
        payload: userUpdatedPayloadSchema,
    }),
    z.object({
        type: z.literal('notification.created'),
        payload: notificationCreatedPayloadSchema,
    }),
    z.object({
        type: z.literal('user.access-revoked'),
        payload: userAccessRevokedPayloadSchema,
    }),
    z.object({
        type: z.literal('admin-user.updated'),
        payload: userUpdatedPayloadSchema,
    }),
    z.object({
        type: z.literal('admin-user.access-revoked'),
        payload: userAccessRevokedPayloadSchema,
    }),
    z.object({
        type: z.literal('audit-log.created'),
        payload: auditLogCreatedPayloadSchema,
    }),
    z.object({
        type: z.literal('api-request-log.created'),
        payload: apiRequestLogCreatedPayloadSchema,
    }),
    z.object({
        type: z.literal('updater-updates.changed'),
        payload: updaterUpdatesChangedPayloadSchema,
    }),
    z.object({
        type: z.literal('updater-transfer.changed'),
        payload: updaterTransferChangedPayloadSchema,
    }),
])

export type SystemSettingsUpdatedEvent = {
    type: 'system-settings.updated'
    payload: {
        companyName: string
        logoSrc?: string
        logoSrc64?: string
        isMaintenanceModeEnabled?: boolean
        maintenanceStartsAt?: string
        updatedAt: string
    }
}

export type UserUpdatedEvent = {
    type: 'user.updated'
    payload: {
        userId: string
        username: string
        previousUsername: string
        permissionKeys: Array<string>
        previousPermissionKeys: Array<string>
        addedPermissionKeys: Array<string>
        removedPermissionKeys: Array<string>
        updatedAt: string
    }
}

export type NotificationCreatedEvent = {
    type: 'notification.created'
    payload: {
        userId: string
        notification: {
            id: string
            title: string
            message: string
            type: string
            referenceId: string | null
            isRead: boolean
            readAt: string | null
            createdAt: string
        }
    }
}

export type MaintenanceModeChangedEvent = {
    type: 'maintenance-mode.changed'
    payload: {
        isMaintenanceModeEnabled: boolean
        isMaintenanceModeActive: boolean
        isMaintenanceModeScheduled: boolean
        maintenanceStartsAt: string | null
        updatedAt: string
    }
}

export type MaintenanceExtensionResolvedEvent = {
    type: 'maintenance-extension.resolved'
    payload: {
        requestId: string
        userId: string
        status: 'approved' | 'rejected'
        maintenanceStartsAt: string | null
        resolvedAt: string
    }
}

export type MaintenanceExtensionRequestedEvent = {
    type: 'maintenance-extension.requested'
    payload: z.infer<typeof maintenanceExtensionRequestedPayloadSchema>
}

export type UserAccessRevokedEvent = {
    type: 'user.access-revoked'
    payload: {
        userId: string
        reason: 'deleted' | 'deactivated'
        updatedAt: string
    }
}

export type AdminUserUpdatedEvent = {
    type: 'admin-user.updated'
    payload: UserUpdatedEvent['payload']
}

export type AdminUserAccessRevokedEvent = {
    type: 'admin-user.access-revoked'
    payload: UserAccessRevokedEvent['payload']
}

export type AuditLogCreatedEvent = {
    type: 'audit-log.created'
    payload: {
        auditLogId: string
        createdAt: string
    }
}

export type ApiRequestLogCreatedEvent = {
    type: 'api-request-log.created'
    payload: z.infer<typeof apiRequestLogCreatedPayloadSchema>
}

export type UpdaterUpdatesChangedEvent = {
    type: 'updater-updates.changed'
    payload: z.infer<typeof updaterUpdatesChangedPayloadSchema>
}

export type UpdaterTransferChangedEvent = {
    type: 'updater-transfer.changed'
    payload: z.infer<typeof updaterTransferChangedPayloadSchema>
}

export type RealtimeEvent = z.infer<typeof realtimeEventSchema>
